import { useTranslation } from 'react-i18next'
import { MaterialSymbol } from 'react-material-symbols'
import { useLocation, useNavigate } from 'react-router-dom'

import { Menu } from 'primereact/menu'
import { MenuItem, MenuItemOptions } from 'primereact/menuitem'

import {
  Permissions,
  useTenantPermissions,
} from '../../../../../features/authorization'
import { isActiveClassName } from '../../../../../utils/helper/navigation/isActiveClassName'
import { MenuItemTemplate } from './MenuItemTemplate'

type Props = {
  handleResize: () => void
  isNavMinimized: boolean
}

export const TenantNavigation: React.FC<Props> = ({
  handleResize,
  isNavMinimized,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const currentLocation = location.pathname
  const { t } = useTranslation()

  const { data: permissions } = useTenantPermissions()

  const buildItems = (): MenuItem[] => {
    const lowerCasePermissions = permissions?.map(p => p.name.toLowerCase())

    let items: MenuItem[] = []

    if (
      lowerCasePermissions?.includes(
        Permissions.Tenant.SupplierCockpit.toLowerCase(),
      )
    ) {
      items.push({
        id: 'menu-item-e-invoice-cockpit',
        label: t('sidebar.purchaser.labels.eInvoiceCockpit'),
        icon: <MaterialSymbol icon="request_quote" size={24} />,
        command: () => {
          navigate('/e-invoice-cockpit')
          handleResize()
        },
        className: isActiveClassName(currentLocation, '/e-invoice-cockpit'),
        template: (item: MenuItem, options: MenuItemOptions) => (
          <MenuItemTemplate
            item={item}
            options={options}
            isNavMinimized={isNavMinimized}
          />
        ),
      })
    }

    if (
      lowerCasePermissions?.includes(
        Permissions.Tenant.SupplierCockpit.toLowerCase(),
      )
    ) {
      items.push({
        id: 'menu-item-suppliers',
        label: t('sidebar.purchaser.labels.supplierCockpit'),
        icon: <MaterialSymbol icon="explore" size={24} />,
        command: () => {
          navigate('/suppliers')
          handleResize()
        },
        className: isActiveClassName(currentLocation, '/suppliers'),
        template: (item: MenuItem, options: MenuItemOptions) => (
          <MenuItemTemplate
            item={item}
            options={options}
            isNavMinimized={isNavMinimized}
          />
        ),
      })
    }

    if (
      lowerCasePermissions?.includes(
        Permissions.Tenant.CollectionUserAdmin.toLowerCase(),
      )
    ) {
      items.push({
        id: 'menu-item-role-assignments',
        label: t('sidebar.purchaser.labels.userManagement'),
        icon: <MaterialSymbol icon="group" size={24} />,
        command: () => {
          navigate('/role-assignments')
          handleResize()
        },
        className: isActiveClassName(
          currentLocation,
          '/role-assignments',
          true,
        ),
        template: (item: MenuItem, options: MenuItemOptions) => (
          <MenuItemTemplate
            item={item}
            options={options}
            isNavMinimized={isNavMinimized}
          />
        ),
      })
    }

    if (
      lowerCasePermissions?.includes(
        Permissions.Tenant.MasterData.toLowerCase(),
      )
    ) {
      items.push({
        id: 'menu-item-role-masterdatamanagement',
        label: t('sidebar.purchaser.labels.masterDataManagement'),
        icon: <MaterialSymbol icon="database" size={24} />,
        command: () => {
          navigate('/master-data-management')
          handleResize()
        },
        className: isActiveClassName(
          currentLocation,
          '/master-data-management',
        ),
        template: (item: MenuItem, options: MenuItemOptions) => (
          <MenuItemTemplate
            item={item}
            options={options}
            isNavMinimized={isNavMinimized}
          />
        ),
      })
    }

    if (
      lowerCasePermissions?.includes(
        Permissions.Tenant.CollectionSettings.toLowerCase(),
      )
    ) {
      items.push({
        id: 'menu-item-buyer-settings',
        label: t('sidebar.purchaser.labels.buyerSettings'),
        icon: <MaterialSymbol icon="settings" size={24} />,
        command: () => {
          navigate('/buyer-settings')
          handleResize()
        },
        className: isActiveClassName(currentLocation, '/buyer-settings', true),
        template: (item: MenuItem, options: MenuItemOptions) => (
          <MenuItemTemplate
            item={item}
            options={options}
            isNavMinimized={isNavMinimized}
          />
        ),
      })
    }

    return [
      {
        label: t('sidebar.purchaser.title'),
        items: items,
      },
    ]
  }

  if (
    !permissions ||
    permissions.filter(p => p.name.toLowerCase().startsWith('webedi')).length <
      1
  )
    return null

  return <Menu model={buildItems()} />
}
